import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Canvas } from '@/canvas/Canvas';
import { Toolbar } from '@/ui/Toolbar';
import { MapListSidebar } from '@/ui/MapListSidebar';
import { AuthPage } from '@/ui/AuthPage';
import { ThemeProvider } from '@/theme/themeContext';
import { useKeybindings } from '@/keybindings/hooks';
import { useDocumentStore, initializeDocument } from '@/store/documentStore';
import { useAuthStore } from '@/store/authStore';
import { commandRegistry } from '@/commands/registry';
import { nodeCommands } from '@/commands/nodeCommands';
import { editCommands } from '@/commands/editCommands';
import { viewCommands } from '@/commands/viewCommands';
import { searchCommands } from '@/commands/searchCommands';
import { connectionCommands } from '@/commands/connectionCommands';
import { exportCommands } from '@/commands/exportCommands';
import { styleCommands } from '@/commands/styleCommands';
import { notesCommands } from '@/commands/notesCommands';
import { attachmentCommands } from '@/commands/attachmentCommands';
import { clipboardCommands } from '@/commands/clipboardCommands';
import { aiCommands } from '@/commands/aiCommands';
import { commentCommands } from '@/commands/commentCommands';
import { useClipboard, setClipboardMapId } from '@/clipboard/useClipboard';
import { SearchBar } from '@/ui/SearchBar';
import { StatusBar } from '@/ui/StatusBar';
import { SharedMapViewer } from '@/ui/SharedMapViewer';
import { ShortcutOverlay } from '@/ui/ShortcutOverlay';
// NodeStylePanel replaced by inline NodeToolbar rendered inside Canvas
import { NotesPanel } from '@/ui/NotesPanel';
import { AttachmentPanel } from '@/ui/AttachmentPanel';
import { AiPanel } from '@/ui/AiPanel';
import { CommentPanel } from '@/ui/CommentPanel';
import { KeyboardLegend } from '@/ui/KeyboardLegend';
import { ThemePicker } from '@/ui/ThemePicker';
import { allThemes } from '@/theme/themes';
import type { ThemeConfig } from '@/theme/types';
import { useThemeStore } from '@/store/themeStore';
import { startAutosave } from '@/persistence/autosave';
import { loadMap } from '@/persistence/hybridPersistence';
import { MindMapDocument } from '@/model/document';
import { MindMapWebSocketProvider } from '@/collaboration/WebSocketProvider';
import { useAwarenessSync } from '@/collaboration/useAwarenessSync';
import { assignColor } from '@/collaboration/awareness';
import { useCollaborationStore } from '@/store/collaborationStore';
import { CollaboratorList } from '@/ui/CollaboratorList';
import { WS_ENDPOINT, IS_BACKEND_CONFIGURED } from '@/api/config';
import { getIdToken } from '@/api/auth';
import { OfflineIndicator } from '@/ui/OfflineIndicator';
import { AccountSettings } from '@/ui/AccountSettings';
import { PricingPage } from '@/ui/PricingPage';
import { usePlanStore } from '@/store/planStore';
import { useAiStore } from '@/store/aiStore';
import { useCommentStore } from '@/store/commentStore';

// Register all commands
commandRegistry.registerAll(nodeCommands);
commandRegistry.registerAll(editCommands);
commandRegistry.registerAll(viewCommands);
commandRegistry.registerAll(searchCommands);
commandRegistry.registerAll(connectionCommands);
commandRegistry.registerAll(exportCommands);
commandRegistry.registerAll(styleCommands);
commandRegistry.registerAll(notesCommands);
commandRegistry.registerAll(attachmentCommands);
commandRegistry.registerAll(clipboardCommands);
commandRegistry.registerAll(aiCommands);
commandRegistry.registerAll(commentCommands);

export default function App() {
  const { t } = useTranslation();
  useKeybindings();
  useAwarenessSync();
  useClipboard();

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const checkSession = useAuthStore((s) => s.checkSession);

  // Check for share token in URL
  const shareToken = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('share');
  }, []);

  const [currentMapId, setCurrentMapId] = useState<string | null>(null);
  const [activeTheme, setActiveTheme] = useState<ThemeConfig>(allThemes[0]!);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [billingMessage, setBillingMessage] = useState<{ type: 'success' | 'cancel'; text: string } | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const fetchPlan = usePlanStore((s) => s.fetchPlan);
  const fetchAiCredits = useAiStore((s) => s.fetchCredits);

  // Keep the clipboard module aware of the current map ID (for file uploads)
  useEffect(() => {
    setClipboardMapId(currentMapId);
  }, [currentMapId]);

  // Sync map theme's canvas background into the CSS variable so the canvas
  // responds to both the app dark/light toggle AND the map theme picker.
  const appTheme = useThemeStore((s) => s.appTheme);

  useEffect(() => {
    if (activeTheme.id === 'default') {
      // Let CSS [data-theme] handle the canvas background
      document.documentElement.style.removeProperty('--bg-canvas');
    } else {
      // Override for custom map themes (ocean, forest, etc.)
      document.documentElement.style.setProperty('--bg-canvas', activeTheme.canvasBackground);
    }
  }, [activeTheme.id, activeTheme.canvasBackground, appTheme]);

  // Restore session on mount (only when backend is configured)
  useEffect(() => {
    if (IS_BACKEND_CONFIGURED) {
      void checkSession();
    }
  }, [checkSession]);

  // Fetch plan and AI credits after authentication
  useEffect(() => {
    if (isAuthenticated && IS_BACKEND_CONFIGURED) {
      void fetchPlan();
      void fetchAiCredits();
    }
  }, [isAuthenticated, fetchPlan, fetchAiCredits]);

  // Handle billing redirect URLs (/billing/success, /billing/cancel)
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/billing/success') {
      setBillingMessage({ type: 'success', text: t('common.subscriptionActivated') });
      window.history.replaceState({}, '', '/');
      // Refresh plan info
      void fetchPlan();
      const timer = setTimeout(() => setBillingMessage(null), 5000);
      return () => clearTimeout(timer);
    }
    if (path === '/billing/cancel') {
      setBillingMessage({ type: 'cancel', text: t('common.checkoutCanceled') });
      window.history.replaceState({}, '', '/');
      const timer = setTimeout(() => setBillingMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [fetchPlan, t]);

  const setupDocument = useCallback((mapDoc: MindMapDocument, mapId: string) => {
    // Clean up previous autosave
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    // Sync Yjs state to Zustand on every change
    const unobserve = mapDoc.observe(() => {
      useDocumentStore.setState((prev) => ({
        nodes: mapDoc.getAllNodes(),
        connections: mapDoc.getAllConnections(),
        meta: mapDoc.getMeta(),
        settings: mapDoc.getSettings(),
        revision: prev.revision + 1,
      }));
    });

    // Initial sync
    useDocumentStore.setState({
      mapDoc,
      nodes: mapDoc.getAllNodes(),
      connections: mapDoc.getAllConnections(),
      meta: mapDoc.getMeta(),
      settings: mapDoc.getSettings(),
      revision: 0,
    });

    // Start autosave
    const stopAutosave = startAutosave(mapDoc, mapId);
    setCurrentMapId(mapId);

    // Remember the last opened map for page reload
    try { localStorage.setItem('ideatwig_last_map', mapId); } catch { /* ignore */ }

    // Fetch comments for this map
    void useCommentStore.getState().fetchComments(mapId);

    // Start real-time collaboration if authenticated and WebSocket endpoint is configured
    const authState = useAuthStore.getState();
    if (authState.isAuthenticated && WS_ENDPOINT) {
      void (async () => {
        const token = await getIdToken();
        if (token && authState.user) {
          const wsProvider = new MindMapWebSocketProvider(mapDoc.doc, {
            wsEndpoint: WS_ENDPOINT,
            mapId,
            authToken: token,
            user: {
              userId: authState.user.userId,
              name: authState.user.name ?? authState.user.email,
            },
          });
          wsProvider.awareness.setLocalStateField('color', assignColor(authState.user.userId));
          wsProvider.connect();
          useCollaborationStore.getState().setProvider(wsProvider);
        }
      })();
    }

    cleanupRef.current = () => {
      unobserve();
      stopAutosave();
      // Clean up provider from the store (may have been set asynchronously)
      const currentProvider = useCollaborationStore.getState().provider;
      if (currentProvider) {
        currentProvider.destroy();
        useCollaborationStore.getState().setProvider(null);
      }
    };
  }, []);

  // On mount: try to reopen the last viewed map, otherwise create a fresh one
  useEffect(() => {
    if (currentMapId !== null) return;
    if (!isAuthenticated && IS_BACKEND_CONFIGURED) return;

    const lastMapId = localStorage.getItem('ideatwig_last_map');
    if (lastMapId && IS_BACKEND_CONFIGURED) {
      // Try to load the last opened map
      void (async () => {
        try {
          const mapDoc = new MindMapDocument();
          await loadMap(lastMapId, mapDoc);
          setupDocument(mapDoc, lastMapId);
        } catch {
          // Map may have been deleted -- fall back to fresh document
          localStorage.removeItem('ideatwig_last_map');
          initializeDocument(t('common.myMindMap'));
        }
      })();
    } else {
      const cleanup = initializeDocument(t('common.myMindMap'));
      return cleanup;
    }
  }, [currentMapId, isAuthenticated, setupDocument, t]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  const handleOpenMap = useCallback(async (id: string) => {
    const mapDoc = new MindMapDocument();
    await loadMap(id, mapDoc);
    setupDocument(mapDoc, id);
  }, [setupDocument]);

  const handleNewMap = useCallback(async (id: string) => {
    try {
      const mapDoc = new MindMapDocument();
      // Try loading from cloud/IndexedDB (the map was already created by the sidebar)
      await loadMap(id, mapDoc);
      setupDocument(mapDoc, id);
    } catch {
      // Fallback: initialize locally if cloud load fails
      const mapDoc = new MindMapDocument();
      mapDoc.initialize(t('common.untitledMap'));
      setupDocument(mapDoc, id);
    }
  }, [setupDocument, t]);

  // If share token is present, render the shared map viewer
  if (shareToken) {
    return <SharedMapViewer token={shareToken} />;
  }

  // Skip auth when backend is not configured (local dev without infrastructure).
  // Production builds always have the backend configured via env vars.
  const skipAuth = !IS_BACKEND_CONFIGURED;

  if (!skipAuth) {
    // Show loading spinner while checking session
    if (isLoading) {
      return (
        <div
          style={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-app)',
            color: 'var(--text-secondary)',
            fontFamily: 'var(--font-family)',
            fontSize: 'var(--font-size-lg)',
          }}
        >
          {t('common.loading')}
        </div>
      );
    }

    // Show auth page if not authenticated
    if (!isAuthenticated) {
      return <AuthPage />;
    }
  }

  if (showAccountSettings) {
    return <AccountSettings onBack={() => setShowAccountSettings(false)} />;
  }

  if (showPricing) {
    return <PricingPage onBack={() => setShowPricing(false)} />;
  }

  return (
    <ThemeProvider theme={activeTheme}>
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
        <OfflineIndicator />
        {billingMessage && (
          <div
            style={{
              position: 'fixed',
              top: 56,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 100,
              background: billingMessage.type === 'success' ? 'var(--bg-success)' : 'var(--bg-error)',
              color: billingMessage.type === 'success' ? '#86efac' : 'var(--text-error)',
              padding: '10px 24px',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-base)',
              fontFamily: 'var(--font-family)',
              boxShadow: 'var(--shadow-md)',
            }}
          >
            {billingMessage.text}
          </div>
        )}
        <Toolbar
          onImported={setupDocument}
          mapId={currentMapId}
          onOpenAccountSettings={() => setShowAccountSettings(true)}
          onOpenPricing={() => setShowPricing(true)}
        />
        <CollaboratorList />
        <MapListSidebar
          currentMapId={currentMapId}
          onOpenMap={(id) => void handleOpenMap(id)}
          onNewMap={(id) => void handleNewMap(id)}
          onOpenPricing={() => setShowPricing(true)}
        />
        <SearchBar />
        <ThemePicker
          currentThemeId={activeTheme.id}
          onThemeChange={(theme) => setActiveTheme(theme)}
        />
        <NotesPanel />
        <AttachmentPanel mapId={currentMapId} />
        <AiPanel />
        <CommentPanel mapId={currentMapId} />
        <ShortcutOverlay />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          <Canvas mapId={currentMapId} />
        </div>
        <KeyboardLegend />
        <StatusBar />
      </div>
    </ThemeProvider>
  );
}
